import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Headphones, Share2, Facebook, ArrowRight, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { getProduct } from "@/lib/catalog";
import SamplePlayer from "@/components/reader/SamplePlayer";

function ShareButtons() {
  const url = `${window.location.origin}/listen`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied — paste it anywhere to share");
  };
  return (
    <div className="flex flex-wrap gap-6">
      <button onClick={copy} data-testid="listen-copy-link" className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors">
        <Share2 className="w-4 h-4" /> Copy link
      </button>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" data-testid="listen-share-facebook"
        className="flex items-center gap-2 font-mono-ui text-xs uppercase tracking-[0.18em] text-soft hover:text-primary-gold transition-colors">
        <Facebook className="w-4 h-4" /> Share on Facebook
      </a>
    </div>
  );
}

function BookSample({ sample, active, onPlay, index }) {
  const book = getProduct(sample.book_id);
  return (
    <article className="grid md:grid-cols-12 gap-8 py-14 border-t border-white/10 fade-up" style={{ animationDelay: `${index * 80}ms` }} data-testid={`listen-book-${sample.book_id}`}>
      <div className="md:col-span-3">
        {book?.cover && <img src={book.cover} alt={sample.title} className="w-32 md:w-full max-w-[200px] aspect-[2/3] object-cover shadow-2xl" />}
      </div>
      <div className="md:col-span-9">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-primary-gold">{sample.chapter}</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 text-text-primary">{sample.title}</h2>
        <p className="font-serif-display text-lg leading-relaxed text-text-primary/80 mt-5 italic max-w-3xl">{sample.excerpt}</p>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {["female", "male"].map((v) => (
            <SamplePlayer key={v} bookId={sample.book_id} voice={v} label={`${v === "female" ? "Female" : "Male"} narrator`} active={active === `${sample.book_id}-${v}`} onPlay={onPlay} />
          ))}
        </div>
      </div>
    </article>
  );
}

function JoinCta({ testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
      <Link to="/reader" data-testid={testId} className="inline-flex items-center gap-3 bg-primary-gold text-black px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.2em] hover:opacity-90 transition-opacity w-fit">
        Join today · $15 for 40 days <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="text-xs text-soft">All four novels · Safe and secure checkout with PayPal</p>
    </div>
  );
}

export default function Listen() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "Free listening samples · The Leckie Novels";
    axios.get(`${API}/samples`).then(({ data }) => setData(data.samples)).catch(() => setData(false));
  }, []);

  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-28" data-testid="listen-page">
      <div className="max-w-3xl fade-up">
        <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold flex items-center gap-2"><Headphones className="w-4 h-4" /> Free 30-second samples</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
          Hear it <span className="text-primary-gold italic">before you buy.</span>
        </h1>
        <p className="text-soft text-base mt-6 leading-relaxed">
          The opening of every Michael Leckie novel, read aloud. Pick the narrator you like — then get all four books
          for $15 and switch voices any time.
        </p>
        <div className="mt-10"><JoinCta testId="listen-cta" /></div>
        <div className="mt-8"><ShareButtons /></div>
      </div>
      <div className="mt-16">
        {data === null && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-primary-gold animate-spin" /></div>}
        {data === false && <p className="text-soft" data-testid="listen-error">The samples aren't available right now.</p>}
        {data && data.map((s, i) => <BookSample key={s.book_id} sample={s} index={i} active={active} onPlay={setActive} />)}
      </div>
      {data && <div className="border-t border-white/10 pt-14"><JoinCta testId="listen-cta-bottom" /></div>}
    </section>
  );
}
